import React, {useEffect, useState, useMemo, useRef} from "react";
import {fetchProductList, fetchVendorList} from "../../store/list-slice";
import Button from "../../components/common/button/Button";
import SubHeader from "../../components/header/SubHeader";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {createPurchase, fetchPurchaseById, updatePurchase} from "../../store/purchase-slice";
import {PaymentType, RoleSign} from "../../enums/enums";
import {fetchVendorById} from "../../store/vendor-slice";
import FileInput from "../../components/common/form/FileInput";
import SelectOption from "../../components/common/form/SelectOption";
import moment from "moment-timezone";

const timezone = 'Asia/Kolkata';
const PurchaseAdd = () => {
    const dispatch = useDispatch();
    const params = useParams();
    const navigator = useNavigate();
    const [purchaseEdit, setPurchaseEdit] = useState(null);
    const [vendorCreator, setVendorCreator] = useState(false);
    const [totalPayment, setTotalPayment] = useState(0);
    const [dueAmount, setDueAmount] = useState(0);
    const [advancedAmount, setAdvancedAmount] = useState(0);
    const [billingError, setBillingError] = useState(false);
    const [productsToPurchase, setProductsToPurchase] = useState([{
        product: '',
        quantity: '',
        price: '',
    }]);

    // check for add duplicate
    const [isDuplicate, setIsDuplicate] = useState(false);

    useEffect(() => {
        // Check if we are duplicating on initial render or when params change
        setIsDuplicate(params.purchaseId && window.location.pathname.endsWith("/add"));
    }, [params.purchaseId]);

    //Basic Form error
    const [errors, setErrors] = useState({
        vendor: '',
        paidAmount: '',
        totalAmount: '',
        purchaseDate: ''
    });

    //Product errors
    const [productErrors, setProductErrors] = useState([
        {quantity: '', price: ''}
    ]);

    const handleBlur = (e) => {
        const {id, value} = e.target;
        validateField(id, value);
    };

    const validateField = (id, value) => {
        let error = "";
        const trimmed = typeof value === "string" ? value.trim() : value;
        switch (id) {
            case "vendor":
                if (!trimmed) {
                    error = "Vendor is required";
                }
                break;

            case "paidAmount":
                if (!String(trimmed)) {
                    error = "Paid amount is required";
                } else {
                    const paid = Number(trimmed);

                    // Check if there are valid products
                    const productIsValid = hasValidProducts();

                    // Only validate against totalAmount if there are NO valid products
                    if (!productIsValid) {
                        const total = Number(formData.totalAmount);
                        if (!isNaN(paid) && !isNaN(total) && paid > total) {
                            error = "Paid amount cannot exceed billing amount";
                        }
                    }
                }
                break;

            case "purchaseDate":
                if (!trimmed) {
                    error = "Purchase date is required";
                }
                break;

            case "totalAmount": {
                // Check if there are valid products
                const productIsValid = hasValidProducts();

                // Only validate totalAmount if there are NO valid products
                if (!productIsValid && !String(trimmed)) {
                    error = "Billing amount is required";
                }
                break;
            }

            default:
                break;
        }

        setErrors((prev) => ({...prev, [id]: error}));

        return error === "";
    };

    //Validate Product row
    const validateProductRow = (product, index) => {
        const errors = {};
        if (product.product) {
            //Add this validation only for packaged products because there quantity always in integer
            if (
                updatedProductListUnit[product.product] === undefined &&
                !Number.isInteger(Number(product.quantity))
            ) {
                errors.quantity = 'Quantity must be a whole number for packaged products';
            }
            if (!product.quantity) errors.quantity = 'Quantity is required';
            if (!product.price || isNaN(product.price)) errors.price = 'Price is required';
        }

        // No else — just don't push errors for empty product

        setProductErrors(prev => {
            const newErrors = [...prev];
            newErrors[index] = errors;
            return newErrors;
        });

        return Object.keys(errors).length === 0;
    };

    const validateAllProducts = () => {
        return productsToPurchase.map((product, index) =>
            validateProductRow(product, index)
        ).every(valid => valid);
    };

    const validateForm = () => {
        let isValid = true;
        Object.entries(formData).forEach(([key, value]) => {
            const valid = validateField(key, value);
            if (!valid) isValid = false;
        });
        return isValid;
    };

    const handleProductBlur = (index, field) => {
        const product = productsToPurchase[index];
        validateProductRow(product, index);
    };

    const {items, createStatus, updateStatus} = useSelector(state => state.purchase);
    const {items: vendor = []} = useSelector(state => state.vendor);
    const userRole = useSelector((state) => state.auth.selectedRole);
    const list = useSelector(state => state.list);
    const managerFieldUpdate = params.purchaseId && userRole.roleSign === RoleSign.Manager;

    const {items: vendors} = list.vendor;
    const {
        items: productsList,
        itemsWithTags
    } = list.product;

    const updatedProductListUnit = itemsWithTags.reduce((acc, item) => {
        acc[item._id] = item.unit;
        return acc;
    }, {});

    const [formData, setFormData] = useState({
        id: userRole.scopeId,
        vendor: params.vendorId ? params.vendorId : '',
        products: [],
        receipt: '',
        totalAmount: '',
        purchaseDate: moment().tz(timezone).format('YYYY-MM-DD'), // default to today,
        paymentMethod: 'Cash',
        paidAmount: 0,
        note: '',
    });

    const productList = formData.vendor ? productsList : [];

    useEffect(() => {
        // Get advanced amount
        if (formData.vendor) {
            const amount = vendor[0]?.advancedAmount;
            setAdvancedAmount(amount);
        }
    }, [vendor, formData.vendor])

    useEffect(() => {
        if (params.purchaseId) {
            dispatch(fetchPurchaseById({...userRole, id: params.purchaseId}));
        }
    }, [dispatch, params.purchaseId, userRole]);

    useEffect(() => {
        // and vendor is not object
        if (formData.vendor && typeof formData.vendor === 'string') {
            dispatch(fetchVendorById({...userRole, id: formData.vendor}))
            dispatch(fetchProductList({...userRole, vendor: formData.vendor}));
        }
    }, [dispatch, userRole, formData.vendor])

    //Want to remove productsToPurchase from the dependency array i.e creating this ref
    const productsRef = useRef(productsToPurchase);
    useEffect(() => {
        if (params.purchaseId && items.length > 0) {
            const found = items.find(item => item.id === params.purchaseId);
            if (found) {
                setPurchaseEdit(found && !(isDuplicate));
                // Pre-fill form data
                setFormData(prevState => ({
                    ...prevState,
                    id: found._id,
                    vendor: found.vendor,
                    receipt: isDuplicate ? '' : found.receipt,
                    totalAmount: found.totalAmount,
                    products: found.products || productsRef.current,
                    purchaseDate: isDuplicate
                        ? moment().tz(timezone).format('YYYY-MM-DD')
                        : (found.purchaseDate ? moment(found.purchaseDate).tz(timezone).format('YYYY-MM-DD') : ''),
                    paymentMethod: found.paymentMethod,
                    paidAmount: isDuplicate ? 0 : found.paidAmount,
                    note: isDuplicate ? '' : found.note || '',
                }));

                setProductsToPurchase(
                    found.products && found.products.length > 0 ? found.products : productsRef.current
                );

                // Set totalPayment and dueAmount separately (not inside formData)
                setTotalPayment(isDuplicate ? 0 : found.totalPayment);
                setDueAmount(isDuplicate ? 0 : found.dueAmount);

                if (found.creator === 'Vendor') {
                    setVendorCreator(true);
                }
            }
        }
    }, [params.purchaseId, isDuplicate, items]);

    const handleFormChange = (name, value) => {
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleProductChange = (index, field, value) => {
        setProductsToPurchase(prev => {
            const updated = [...prev];
            updated[index] = {...updated[index], [field]: value};
            return updated;
        });
    };

    const handleAddRow = () => {
        setProductsToPurchase(prev => [...prev, {product: '', quantity: '', price: ''}]);
        setProductErrors(prev => [...prev, {quantity: '', price: ''}]);
    };

    const handleRemoveRow = (index) => {
        setProductsToPurchase(prev => prev.filter((_, i) => i !== index));
        setProductErrors(prev => prev.filter((_, i) => i !== index));
    };

    const handleDateChange = (e) => {
        const {name, value} = e.target;

        if (name === "purchaseDate") {
            if (!value) {
                // If input is cleared, remove date
                setFormData((prev) => ({
                    ...prev,
                    purchaseDate: "",
                }));
                return;
            }
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const hasValidProducts = () => {
        return productsToPurchase.some(p =>
            p.product?.trim() !== "" &&
            p.quantity?.toString().trim() !== "" &&
            p.price?.toString().trim() !== ""
        );
    };


    const handleSubmit = (event) => {
        event.preventDefault();

        const isFormValid = validateForm();
        const areProductsValid = validateAllProducts();

        if (!isFormValid || !areProductsValid) return;

        const formDataToSend = new FormData();

        // detect if there are any valid products
        const productIsValid = hasValidProducts();

        //when the purchase order is created by vendor
        if (vendorCreator && productIsValid) {
            if (formData.totalAmount !== totalAmount) {
                setBillingError(true)
                return;
            }
        }

        if (productIsValid) {
            // Only allow submit if totalAmount from products >= paidAmount
            if (totalAmount < Number(formData.paidAmount)) {
                setErrors(prev => ({
                    ...prev,
                    paidAmount: "Paid amount cannot exceed outstanding payment"
                }));
                return;
            }
        }

        // Append non-product fields
        Object.entries(formData).forEach(([key, value]) => {
            if (key === 'products') return; // skip here, handle separately

            // skip totalAmount if there are valid products
            if (key === "totalAmount" && productIsValid) return;

            if (value !== null && value !== undefined) {
                if (value instanceof FileList) {
                    Array.from(value).forEach(file => formDataToSend.append(key, file));
                } else {
                    formDataToSend.append(key, value);
                }
            }
        });

        // Append products in array format for backend
        productsToPurchase.forEach((product, index) => {
            //dont append if product is empty
            const allEmpty = Object.values(product).every(val => val === '');

            if (!allEmpty) {
                Object.entries(product).forEach(([field, val]) => {
                    formDataToSend.append(`products[${index}][${field}]`, val);
                });
            }
        });

        if (purchaseEdit && !isDuplicate) {
            // Edit existing purchase
            dispatch(updatePurchase({
                ...userRole,
                data: formDataToSend,
                id: params.purchaseId
            }));
        } else {
            // Remove existing `id` if it exists in the FormData
            formDataToSend.delete("id");

            // Replace with the scopeId from the current userRole
            formDataToSend.append("id", userRole.scopeId);

            // Add or duplicate
            dispatch(createPurchase({
                ...userRole,
                data: formDataToSend,
            }));
        }
    };

    const totalAmount = useMemo(() => {
        return productsToPurchase.reduce((sum, item) => {
            const price = parseFloat(item.price) || 0;
            return sum + price;
        }, 0);
    }, [productsToPurchase]);

    useEffect(() => {
        if (createStatus === "success" || updateStatus === "success") {
            if (params?.vendorId) {
                navigator(`/vendor/${params.vendorId}/purchase`);
            } else {
                navigator('/purchase');
            }
        }
    }, [createStatus, updateStatus, navigator, params?.vendorId]);


    useEffect(() => {
        dispatch(fetchVendorList(userRole));
    }, [dispatch, userRole]);

    return (
        <>
            <SubHeader title={`${purchaseEdit ? 'Update Purchase' : 'Purchase'}`}>
                {params.vendorId ? <Button to={`/vendor/${params.vendorId}/purchase`}
                                           className="btn-sm bg-secondary bg-opacity-50">Back</Button> :
                    <Button to="/purchase" className="btn-sm bg-secondary bg-opacity-50">Back</Button>}
            </SubHeader>
            <form onSubmit={handleSubmit}>
                <div className="row gap-6">
                    <div className="col-12 col-lg-8 col-xl-8">
                        <div className="row align-items-start g-4">
                            {/* Vendor dropdown */}
                            {(!managerFieldUpdate) && <div className="col-md-4">
                                <div className="form-group">
                                    <label className="form-label opacity-75">Vendor</label>
                                    <SelectOption
                                        className="form-select"
                                        id="vendor"
                                        name="vendor"
                                        value={formData.vendor}
                                        onBlur={handleBlur}
                                        isSearch={true}
                                        options={vendors}
                                        onChange={(e) => handleFormChange("vendor", e.target.value)}
                                        disabled={purchaseEdit}
                                    />
                                    {errors.vendor &&
                                        <div className="invalid-feedback d-block">{errors.vendor}</div>}
                                </div>
                            </div>}
                            {/*Paid Amount*/}
                            {(!managerFieldUpdate) && <div className="col-md-4">
                                <div className="form-group">
                                    <label className="form-label opacity-75">Initial Paid Amount</label>
                                    <input
                                        className="form-control"
                                        id="paidAmount"
                                        name="paidAmount"
                                        type="text"
                                        onBlur={handleBlur}
                                        value={formData.paidAmount}
                                        onChange={(e) => handleFormChange("paidAmount", e.target.value)}
                                        disabled={purchaseEdit}
                                    />
                                    {errors.paidAmount &&
                                        <div className="invalid-feedback d-block">{errors.paidAmount}</div>}
                                </div>
                            </div>}
                            {/* Payment Method */}
                            {(!managerFieldUpdate) && <div className="col-md-4">
                                <div>
                                    <label className="form-label opacity-75">Payment Method</label>
                                    <SelectOption
                                        className="form-select"
                                        name="paymentMethod"
                                        value={formData.paymentMethod}
                                        enums={true}
                                        options={PaymentType}
                                        onChange={(e) => handleFormChange("paymentMethod", e.target.value)}
                                    />
                                </div>
                            </div>}
                            {(!managerFieldUpdate) &&
                                <>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <label className="form-label opacity-75">Purchase Date</label>
                                            <input
                                                className="form-control"
                                                id="purchaseDate"
                                                name="purchaseDate"
                                                type="date"
                                                onBlur={handleBlur}
                                                value={moment(formData.purchaseDate).tz(timezone).format('YYYY-MM-DD')}
                                                onChange={handleDateChange}
                                            />
                                            {errors.purchaseDate &&
                                                <div className="invalid-feedback d-block">{errors.purchaseDate}</div>}
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="form-group">
                                            <FileInput
                                                name="receipt"
                                                value={formData.receipt}
                                                label="File"
                                                placeholder="Choose an image..."
                                                onChange={(e) =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        [e.target.name]: e.target.value
                                                    }))
                                                }
                                            />
                                        </div>
                                    </div>
                                    {/*Billing Amount*/}
                                    {(!managerFieldUpdate && (!hasValidProducts() || vendorCreator)) && (
                                        <div className="col-md-4">
                                            <div className="form-group">
                                                <label className="form-label opacity-75">Billing Amount</label>
                                                <input
                                                    className="form-control"
                                                    id="totalAmount"
                                                    name="totalAmount"
                                                    type="text"
                                                    onBlur={handleBlur}
                                                    value={formData.totalAmount}
                                                    onChange={(e) => handleFormChange("totalAmount", e.target.value)}
                                                    disabled={vendorCreator}
                                                />
                                                {errors.totalAmount && (
                                                    <div className="invalid-feedback d-block">
                                                        {errors.totalAmount}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                    }
                                </>}
                            {/*Notes*/}
                            <div className="col-md-8">
                                <div>
                                    <label className="form-label opacity-75">Note</label>
                                    <textarea
                                        className="form-control"
                                        name="note"
                                        rows="1"
                                        type="textarea"
                                        value={formData.note}
                                        onChange={(e) => handleFormChange("note", e.target.value)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {/*Add Products*/}
                    <div className="col-lg-10">
                        <div className="d-flex">
                            <div className="d-flex">
                                <h5 className="fw-md fs-6 text-info text-end mb-1 me-5">Total Paid Payment:
                                    ₹{totalPayment}</h5>
                                {dueAmount > 0 &&
                                    <h5 className="fw-md fs-6 text-danger text-end mb-1">Due Amount: ₹{dueAmount}</h5>}
                            </div>
                            {advancedAmount > 0 &&
                                <h5 className="fw-md fs-6 text-primary text-end mb-1">Advanced amount:
                                    ₹ {advancedAmount}/-</h5>}
                        </div>
                        {(!managerFieldUpdate) && <>
                            <div className="d-flex justify-content-between">
                                <h6 className="fw-md fs-6">Add Stock</h6>
                                <h5 className="fw-md fs-6 text-secondary">Outstanding payment: ₹ {totalAmount}/-</h5>
                            </div>
                            {/*Error*/}
                            {billingError && (
                                <p className="invalid-feedback text-end d-block">
                                    Selected product total doesn’t match the vendor’s bill <span
                                    className="fw-semibold">(₹ {formData.totalAmount}/- </span>)
                                </p>
                            )}
                            {productsToPurchase.map((item, index) => (
                                <div key={index} className="row mb-3 mt-3 row-gap-2 card-sm">
                                    <div className="col-lg-3">
                                        {/* Product Select */}
                                        <SelectOption
                                            className="form-select"
                                            name="product"
                                            value={item.product}
                                            options={productList}
                                            isSearch={true}
                                            onChange={(e) => handleProductChange(index, "product", e.target.value)}
                                        />
                                    </div>

                                    <div className="col-lg-3">
                                        {/* Quantity */}
                                        <input
                                            className="form-control"
                                            name="quantity"
                                            type="text"
                                            placeholder="Enter quantity"
                                            value={item.quantity}
                                            onBlur={() => handleProductBlur(index, "quantity")}
                                            onChange={(e) => handleProductChange(index, "quantity", e.target.value)}
                                        />
                                        {productErrors[index]?.quantity && (
                                            <div
                                                className="invalid-feedback d-block">{productErrors[index].quantity}</div>
                                        )}
                                    </div>

                                    <div className="col-lg-2">
                                        {/* Price  */}
                                        <input
                                            className="form-control"
                                            name="price"
                                            type="text"
                                            placeholder="Enter price"
                                            value={item.price}
                                            onBlur={() => handleProductBlur(index, "price")}
                                            onChange={(e) => handleProductChange(index, "price", e.target.value)}
                                        />
                                        {productErrors[index]?.price && (
                                            <div className="invalid-feedback d-block">{productErrors[index].price}</div>
                                        )}
                                    </div>

                                    <div className="col-lg-2">
                                        {/* Per Unit Price  */}
                                        <div className="position-relative">
                                            <input
                                                className="form-control text-secondary"
                                                name="Unit price"
                                                type="text"
                                                placeholder="Enter price"
                                                value={Number((item.price / item.quantity || 0).toFixed(2))}
                                                disabled
                                            />
                                            <span className="position-absolute end-0 top-0 h-100 pe-3 text-secondary"
                                                  style={{paddingTop: 6}}>
                                                  per {updatedProductListUnit[item.product] !== null ? updatedProductListUnit[item.product] : "unit"}{item.product === '' ? "unit" : ""}
                                            </span>

                                        </div>
                                    </div>

                                    <div className="col-lg-2">
                                        {/* Add/Remove Buttons */}
                                        <button className="btn btn-outline-success me-2" type="button"
                                                onClick={handleAddRow}>
                                            +
                                        </button>
                                        {productsToPurchase.length > 1 && (
                                            <button
                                                className="btn btn-outline-danger"
                                                type="button"
                                                onClick={() => handleRemoveRow(index)}
                                            >
                                                −
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </>}
                        <button
                            type="submit"
                            className="btn btn-primary mt-3"
                            disabled={createStatus === 'loading' || updateStatus === 'loading'}
                        >
                            Submit
                        </button>
                    </div>
                </div>
            </form>
        </>
    )
}

export default PurchaseAdd;
